import { MatDialog } from '@angular/material/dialog';
import { Observable, from, of } from 'rxjs';
import { concatMap, map, toArray } from 'rxjs/operators';

import {
  ExportScope, TableExportDialogComponent,
} from '../components/table-export-dialog/table-export-dialog.component';
import { CsvColumn, downloadCsv } from './csv-export';

/**
 * What a page has to be able to answer for its table to be exportable.
 *
 * All functions rather than values: an export reads them at the moment somebody
 * presses the button, so a column picked, a row ticked or a filter typed since
 * the page loaded counts. A snapshot taken when this was constructed would
 * export the table as it was when the page opened.
 */
export interface TableExportSource<T> {
  /** What the rows are called in the dialog's sentence: "leads", "customers". */
  readonly title: string;
  /** What the saved file is called, built when the export runs. */
  filename: () => string;
  /** The file's columns, in the order they are written. */
  columns: () => readonly CsvColumn<T>[];
  /** Ticked rows. Empty when nothing is ticked. */
  selection: () => readonly T[];
  /** The rows the table is showing right now. */
  pageRows: () => readonly T[];
  /** How many rows the current filter matches, which may exceed the page. */
  filteredTotal: () => number;
  /**
   * How many rows the table holds with NO filter, or null when the server
   * would not say. Must not error: the dialog opens either way.
   */
  unfilteredTotal: () => Observable<number | null>;
  /**
   * The server's own maximum page size for this endpoint.
   *
   * Its MAXIMUM, not a number that seems large: a server that clamps silently
   * hands back a shorter page than was asked for, and the walk below - which
   * works out how many pages there are by dividing by this - would then stop
   * after a fraction of the table and write a file that looks complete.
   */
  readonly pageSize: number;
  /** One page for the export. `filtered` false means the whole table. */
  fetchPage: (page: number, filtered: boolean) => Observable<T[]>;
}

/**
 * The Export button's whole behaviour: ask which rows, gather them, write the
 * file.
 *
 * A plain class rather than a component because the pages that export differ
 * in where the button sits and what else is beside it, but not in any of this.
 * The page keeps the button and delegates; what it does not keep is a second
 * copy of the paging walk or the CSV rules.
 */
export class TableExport<T> {
  /** True while rows are being read. The button reads this to disable itself. */
  busy = false;

  /** Set when a gather failed, for the page to show. Cleared on the next try. */
  error: string | null = null;

  constructor(
    private readonly source: TableExportSource<T>,
    private readonly dialog?: MatDialog,
  ) {}

  /**
   * Ask which rows, then write them.
   *
   * The question exists because the table is database-paged and "what I can
   * see" is not "what I asked for". It is asked HERE, at the moment somebody
   * says Export, rather than standing on the command band being answered in
   * advance by people who did not know they were answering it.
   */
  open(): void {
    if (this.busy) return;
    this.error = null;
    // How many rows the table holds with no filter, asked before the dialog
    // opens so it can say whether "all the table" is a different set from
    // "everything matching the filter". If it fails the dialog still opens - a
    // count we could not get is a reason to leave that option out, not to
    // refuse the export.
    this.source.unfilteredTotal().subscribe(tableCount => {
      this.dialog?.open(TableExportDialogComponent, {
        autoFocus: false,
        data: {
          title: this.source.title,
          selectedCount: this.source.selection().length,
          pageCount: this.source.pageRows().length,
          totalCount: this.source.filteredTotal(),
          tableCount,
        },
      })
        .afterClosed()
        .subscribe(scope => { if (scope) this.run(scope); });
    });
  }

  /**
   * Writes the file for a scope the caller has already settled.
   *
   * The scope is a parameter rather than state on this object, because the
   * only thing that decides it is the dialog and the only thing that reads it
   * is this: a field between them would be a place for the two to disagree.
   * Called with nothing it falls back to what a ticked table means on its own,
   * which is the rows that are ticked.
   */
  run(scope?: ExportScope): void {
    if (this.busy) return;
    this.error = null;
    const chosen = scope ?? (this.source.selection().length ? 'selection' : 'page');
    // Either of the two that reach past the page is fetched before anything is
    // written. The other two are already in hand.
    if (chosen === 'all' || chosen === 'table') {
      this.busy = true;
      this.everyRow(chosen === 'all').subscribe({
        next: rows => { this.busy = false; this.write(rows); },
        error: () => {
          this.busy = false;
          this.error = 'Could not read the whole table to export it.';
        },
      });
      return;
    }
    this.write(chosen === 'selection'
      ? this.source.selection()
      : this.source.pageRows());
  }

  /**
   * Every row in the chosen set, a server page at a time.
   *
   * Sequentially, not in parallel: this is one person pressing Export, and a
   * burst of concurrent page reads would cost the shared database more than
   * the wait costs them.
   */
  private everyRow(filtered: boolean): Observable<T[]> {
    // How many pages there are depends on which set is being read, and reading
    // the filtered count while fetching unfiltered rows would stop short of
    // the table. So the unfiltered walk asks the server for its own total
    // first rather than reusing the filtered one.
    const total = filtered
      ? of(this.source.filteredTotal())
      : this.source.unfilteredTotal().pipe(
          map(count => count ?? this.source.filteredTotal()));
    return total.pipe(
      concatMap(rows => {
        const pages = Math.max(1, Math.ceil(rows / this.source.pageSize));
        return from(Array.from({ length: pages }, (_, i) => i + 1)).pipe(
          concatMap(page => this.source.fetchPage(page, filtered)),
          toArray(),
          map(chunks => chunks.flat()),
        );
      }),
    );
  }

  private write(rows: readonly T[]): void {
    downloadCsv(this.source.columns(), rows, this.source.filename());
  }
}
